import { defaultAllSelection } from "services/constants/selections";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useSelector } from "react-redux";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import CustomDateRangePicker from "components/CustomComponents/CustomDateRangePicker";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestEditHeader } from "components/DesignSystem/RequestEditHeader";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import { showToast } from "services/utils/Status";
import { leavePalette } from "../Leave/leaveHelpers";
import { designColors } from "services/constants/designTokens";
import { SCHEDULE_REQUEST_TYPES } from "../ShiftCorrection/shiftCorrectionHelpers";
import {
  DIRECTION_OPTIONS,
  deriveInOut,
  formatPunchTime,
  isOvernightShift,
  makePunchRow,
  withOvernightDate,
} from "./correctionApplyHelpers";

dayjs.extend(customParseFormat);

// The shared "Edit Attendance" style apply panel (reference image 4), used by
// both Attendance Correction and Shift Change. It renders:
//   - the employee context card (UserDetailsRow),
//   - the AR Date Range selector for Attendance Correction,
//   - the Shift Change selector block for shift requests,
//   - a Reason field, and a Reset / Apply footer.
// It stays presentation-only: on submit it hands the parent the raw form data
// via `buildRecord`, so each screen maps it to its own record shape and there is
// exactly one request panel to maintain.
const sectionLabelSx = { fontWeight: 600, color: leavePalette.text };
const helperSx = { fontSize: 12.5, color: leavePalette.subText, mb: 0.75 };
const DATE_DISPLAY_FMT = "DD-MM-YYYY";
const DATE_DASH_FMT = "DD-MM-YYYY";
const DATE_API_FMT = "YYYY-MM-DD";
const DATE_PARSE_FORMATS = [DATE_DISPLAY_FMT, "DD/MM/YYYY", DATE_API_FMT];

const REASON_MAX_LENGTH = 255;

const todayDisplay = () => dayjs().format(DATE_DISPLAY_FMT);

const toDayjsDate = (date) => {
  if (!date) return null;
  if (dayjs.isDayjs(date)) return date.isValid() ? date : null;
  const parsed = dayjs(date, DATE_PARSE_FORMATS, true);
  if (parsed.isValid()) return parsed;
  const fallback = dayjs(date);
  return fallback.isValid() ? fallback : null;
};

const displayDateToDayjs = toDayjsDate;

const toDayjsTime = (time) => {
  if (!time) return null;
  if (dayjs.isDayjs(time)) return time.isValid() ? time : null;
  const parsed = dayjs(
    String(time),
    ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"],
    true,
  );
  if (parsed.isValid()) return parsed;
  const fallback = dayjs(time);
  return fallback.isValid() ? fallback : null;
};

const datesBetween = (startDate, endDate) => {
  const start = toDayjsDate(startDate);
  const end = toDayjsDate(endDate) || start;
  if (!start || !end) return [];

  const dates = [];
  let cursor = start.startOf("day");
  const last = end.startOf("day");
  while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
    dates.push(cursor.format(DATE_API_FMT));
    cursor = cursor.add(1, "day");
  }
  return dates;
};

const normalizePunchRow = (entry, index, baseDate) => ({
  ...entry,
  rowId: entry?.rowId || entry?.punch_id || index + 1,
  date: toDayjsDate(entry?.date) || baseDate || dayjs(),
  time: toDayjsTime(entry?.time),
  direction: (
    entry?.direction || (index % 2 === 0 ? "IN" : "OUT")
  ).toUpperCase(),
});

const normalizeAttendanceDay = (
  response = {},
  apiDate,
  index = 0,
  editEntries = null,
  editOriginalEntries = null,
  editEarlyExit = null,
) => {
  const baseDate =
    toDayjsDate(response?.date) || toDayjsDate(apiDate) || dayjs();
  const responseOriginalEntries = Array.isArray(response?.punch_entries)
    ? response.punch_entries.map((entry, entryIndex) =>
        normalizePunchRow(entry, entryIndex, baseDate),
      )
    : [];
  const originalEntries = Array.isArray(editOriginalEntries)
    ? editOriginalEntries.map((entry, entryIndex) =>
        normalizePunchRow(entry, entryIndex, baseDate),
      )
    : responseOriginalEntries;
  // When editing an existing correction request, the actual attendance record
  // fetched above is the (missing/incorrect) punch log the request was raised
  // against, so it's normal for it to come back empty. The editable rows
  // should instead start from the request's own previously-submitted entries.
  const requestedEntries = Array.isArray(editEntries)
    ? editEntries.map((entry, entryIndex) =>
        normalizePunchRow(entry, entryIndex, baseDate),
      )
    : [];

  return {
    ...response,
    id: index + 1,
    api_date: baseDate.format(DATE_API_FMT),
    date: baseDate.format(DATE_DASH_FMT),
    display_date: baseDate.format(DATE_DISPLAY_FMT),
    // On a week off the API still returns the fallback shift it assigns for
    // processing, so show "Week Off" instead of that shift's name/times. With
    // no shift at all, say so rather than inventing one.
    shift: response?.is_week_off ? null : (response?.shift ?? null),
    shift_name: response?.is_week_off
      ? "Week Off"
      : response?.shift_name || "No shift assigned",
    shift_start: response?.is_week_off ? null : response?.shift_start || null,
    shift_end: response?.is_week_off ? null : response?.shift_end || null,
    // New requests start without punch rows; the user adds corrections with +.
    // Keep saved correction rows when editing an existing request.
    punch_entries: requestedEntries.map((entry) => ({ ...entry })),
    original_punch_entries: originalEntries,
    early_exit:
      editEarlyExit !== null && editEarlyExit !== undefined
        ? Boolean(editEarlyExit)
        : Boolean(response?.early_exit),
    error: "",
  };
};

const cloneAttendanceDays = (days = []) =>
  days.map((day) => ({
    ...day,
    punch_entries: (day.punch_entries || []).map((entry) => ({
      ...entry,
      date: entry.date ? dayjs(entry.date) : null,
      time: entry.time ? dayjs(entry.time) : null,
    })),
    original_punch_entries: (day.original_punch_entries || []).map((entry) => ({
      ...entry,
      date: entry.date ? dayjs(entry.date) : null,
      time: entry.time ? dayjs(entry.time) : null,
    })),
  }));

const hasTimedPunch = (entries = []) =>
  entries.some((entry) => entry.time && dayjs(entry.time).isValid());

const punchDirectionSx = (direction) => {
  const isIn = String(direction || "").toUpperCase() === "IN";
  return {
    px: 1,
    py: 0.35,
    borderRadius: 1,
    bgcolor: isIn ? "#ECFDF3" : "#FFF7ED",
    color: isIn ? "#039855" : "#EA580C",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
  };
};

// Column widths shared by the read-only Current Punches rows and the editable
// Corrected Punches rows, so Date / Time / Direction line up between the two.
// The trailing spacer stands in for a corrected row's remove button.
const PUNCH_COLUMN_FLEX = { date: 1.2, time: 1, direction: 0.9 };
const PUNCH_REMOVE_SLOT_WIDTH = 30;
// Matches the left padding of the pickers' input text.
const punchCellSx = { pl: 1.75 };

const CurrentPunchesTable = ({ entries = [] }) => {
  if (!entries.length) {
    return (
      <Typography
        sx={{ fontSize: 13, color: leavePalette.subText, px: 1, py: 0.5 }}
      >
        No punches on record
      </Typography>
    );
  }

  return (
    <Stack>
      {entries.map((entry) => (
        <Stack
          key={entry.rowId}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1,
            py: 0.8,
            borderBottom: `1px solid ${leavePalette.border}`,
          }}
        >
          <Box sx={{ flex: PUNCH_COLUMN_FLEX.date, ...punchCellSx }}>
            <Typography sx={{ fontSize: 13, color: leavePalette.text }}>
              {entry.date && dayjs(entry.date).isValid()
                ? dayjs(entry.date).format(DATE_DISPLAY_FMT)
                : "--"}
            </Typography>
          </Box>
          <Box sx={{ flex: PUNCH_COLUMN_FLEX.time, ...punchCellSx }}>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: leavePalette.text }}
            >
              {formatPunchTime(entry.time) || "--"}
            </Typography>
          </Box>
          <Box sx={{ flex: PUNCH_COLUMN_FLEX.direction, ...punchCellSx }}>
            <Box component="span" sx={punchDirectionSx(entry.direction)}>
              {entry.direction || "-"}
            </Box>
          </Box>
          <Box sx={{ width: PUNCH_REMOVE_SLOT_WIDTH, flexShrink: 0 }} />
        </Stack>
      ))}
    </Stack>
  );
};

const toPunchPayloadRows = (entries = [], fallbackDate) =>
  entries
    .filter((entry) => entry.time && dayjs(entry.time).isValid())
    .map((entry) => ({
      date:
        entry.date && dayjs(entry.date).isValid()
          ? dayjs(entry.date).format(DATE_API_FMT)
          : fallbackDate,
      time: dayjs(entry.time).format("HH:mm"),
      direction: entry.direction,
    }));

// A day's punches can be dated from its AR date up to the next day (an
// overnight shift's after-midnight punches), but never after today.
const maxPunchDateFor = (apiDate) => {
  const base = toDayjsDate(apiDate);
  if (!base) return null;
  return base.isSame(dayjs(), "day") ? dayjs() : base.add(1, "day");
};

const makeOpenState = (days = [], open = true) =>
  days.reduce((acc, day) => {
    acc[day.api_date] = open;
    return acc;
  }, {});

// Seed the date-range state from an existing request's date when editing,
// falling back to today for a brand-new request. Both "YYYY-MM-DD" and
// "DD-MM-YYYY" are accepted -- an unparsed date used to silently fall back to
// today, so editing an old request opened today's attendance instead.
const initialDisplayDate = (date) => {
  const parsed = date
    ? dayjs(date, [DATE_API_FMT, DATE_DISPLAY_FMT], true)
    : null;
  return parsed && parsed.isValid()
    ? parsed.format(DATE_DISPLAY_FMT)
    : todayDisplay();
};

// In time order, a day's corrected punches must start with IN and alternate
// IN/OUT -- approval replaces the day's whole punch set with them. Returns an
// error message, or "".
const punchSequenceError = (day) => {
  const timed = (day.punch_entries || [])
    .filter((entry) => entry.time && dayjs(entry.time).isValid())
    .map((entry) => {
      const date =
        entry.date && dayjs(entry.date).isValid() ? dayjs(entry.date) : null;
      const time = dayjs(entry.time);
      const at = (date || toDayjsDate(day.api_date) || dayjs())
        .hour(time.hour())
        .minute(time.minute())
        .second(0);
      return { at, direction: entry.direction };
    })
    .sort((a, b) => a.at.valueOf() - b.at.valueOf());
  let expected = "IN";
  for (const punch of timed) {
    if (punch.direction !== expected) {
      return punch.direction === "OUT"
        ? `${day.display_date}: OUT at ${punch.at.format("HH:mm")} has no IN before it. Punches must start with IN and alternate IN/OUT.`
        : `${day.display_date}: two IN punches in a row (at ${punch.at.format("HH:mm")}). Punches must alternate IN/OUT.`;
    }
    expected = expected === "IN" ? "OUT" : "IN";
  }
  return "";
};

export const CorrectionApplyPanel = ({
  open = true,
  onClose,
  onCreate,
  title,
  subtitle,
  defaultCorrectionType = "",
  mode = "attendance-correction",
  buildRecord,
  // When set, the panel edits this existing request instead of creating a new
  // one: the date range is seeded from it and, for shift-change, so is the
  // requested/assigned shift. Submitting PUTs (shift-change) or re-POSTs
  // (attendance-correction, which already replaces the Pending row for that
  // date) instead of always creating a fresh request.
  editRecord = null,
  // When set (Team Workspace editing an existing request, or an employee
  // already resolved some other way), the panel's attendance/shift lookups
  // and the submitted request's `user_id` operate on this employee instead
  // of the logged-in user (`userDetails`).
  targetUser = null,
  // Team Workspace's own "apply on behalf of an employee" flow (attendance
  // correction only): renders an Employee Name picker as the first field and
  // uses the picked employee as the effective user instead of `userDetails`.
  // Ignored once `targetUser` or `editRecord` is provided.
  showEmployeePicker = false,
  // A manager raising this request already IS the approval -- unlike an
  // employee's own self-service submission, there's no one else left to
  // approve it. When set, the footer button reads "Approve" and, once the
  // Pending request is created, this panel immediately approves it via the
  // Team Zone endpoint instead of leaving it Pending for a separate action.
  approveOnCreate = false,
}) => {
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const isShiftChange = mode === "shift-change";
  const isEditing = Boolean(editRecord);
  // The edited request's own date as "YYYY-MM-DD", whichever format it came in.
  const editApiDate = editRecord?.date
    ? dayjs(editRecord.date, [DATE_API_FMT, DATE_DISPLAY_FMT], true).isValid()
      ? dayjs(editRecord.date, [DATE_API_FMT, DATE_DISPLAY_FMT], true).format(
          DATE_API_FMT,
        )
      : null
    : null;
  const [pickedEmployee, setPickedEmployee] = useState(null);
  const employeePickerActive = showEmployeePicker && !isEditing && !targetUser;
  // While the picker is active, don't fall back to the logged-in user -- stay
  // null (blocking the attendance fetch/submit below) until an employee is
  // actually picked, rather than silently loading/submitting for the wrong
  // person (the HR user themselves).
  const effectiveUser =
    targetUser || (employeePickerActive ? pickedEmployee : userDetails);

  const { result: pickerEmployees } = useFetch(
    employeePickerActive
      ? `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`
      : null,
  );
  const [requestType, setRequestType] = useState(
    editRecord?.request_type || "shift_change",
  );
  const isWeekOffSwap = isShiftChange && requestType === "week_off_swap";
  const panelTitle = isShiftChange
    ? "Schedule Change Request"
    : "Attendance Correction Request";
  const panelSubtitle = isShiftChange
    ? isEditing
      ? "Update the schedule change request."
      : "Create a request to change your shift or swap your week-off."
    : subtitle;

  const [fromDate, setFromDate] = useState(() =>
    initialDisplayDate(editRecord?.date),
  );
  const [toDate, setToDate] = useState(() =>
    initialDisplayDate(editRecord?.date),
  );
  const [swapDate, setSwapDate] = useState(() =>
    initialDisplayDate(editRecord?.swap_date || editRecord?.to_date),
  );
  const [shiftId, setShiftId] = useState(
    () => editRecord?.requested_shift_id || "",
  );
  const [shiftOptions, setShiftOptions] = useState([]);
  const [shiftOptionsLoading, setShiftOptionsLoading] = useState(false);
  // The user's currently-assigned shift on the request date, resolved from
  // daily_user_shift_assignments and sent as `assigned_shift_id` (shift change).
  const [assignedShiftId, setAssignedShiftId] = useState(
    editRecord?.assigned_shift_id || null,
  );
  const [assignedShiftName, setAssignedShiftName] = useState(
    editRecord?.original_shift || "",
  );
  const [swapShift, setSwapShift] = useState(null);
  const [attendanceDays, setAttendanceDays] = useState([]);
  // Latest days, read by the range-change refetch below so punches already
  // entered for a date that stays in the new range are kept, not wiped.
  const attendanceDaysRef = useRef([]);
  attendanceDaysRef.current = attendanceDays;
  const [defaultAttendanceDays, setDefaultAttendanceDays] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceLoadError, setAttendanceLoadError] = useState("");
  const [openPunchDays, setOpenPunchDays] = useState({});
  const [reason, setReason] = useState(() => editRecord?.reason || "");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedShift = useMemo(
    () => shiftOptions.find((option) => option.id === shiftId) || null,
    [shiftId, shiftOptions],
  );

  const rangeStartDate = displayDateToDayjs(fromDate);
  const rangeEndDate = displayDateToDayjs(toDate) || rangeStartDate;
  const requestDate = rangeStartDate
    ? rangeStartDate.format(DATE_API_FMT)
    : null;
  const requestEndDate = rangeEndDate
    ? rangeEndDate.format(DATE_API_FMT)
    : requestDate;
  const swapDay = displayDateToDayjs(swapDate);
  const swapRequestDate = swapDay ? swapDay.format(DATE_API_FMT) : null;
  const rangeDates = useMemo(
    () => datesBetween(requestDate, requestEndDate),
    [requestDate, requestEndDate],
  );

  useEffect(() => {
    if (
      isShiftChange ||
      !open ||
      !domain ||
      !effectiveUser?.id ||
      !requestDate ||
      !requestEndDate
    ) {
      return undefined;
    }

    let active = true;

    const fetchAttendanceDays = async () => {
      setAttendanceLoading(true);
      setAttendanceLoadError("");

      const loaded = await Promise.all(
        rangeDates.map(async (date, index) => {
          const response = await customGetAPI(
            `${domain}attendance_manual_entry/?user_id=${effectiveUser.id}&date=${date}&company_id=${topbarCompanyId}`,
          );
          return {
            ok: Boolean(response?.ok),
            day: normalizeAttendanceDay(
              response?.ok ? response.responseDetails : {},
              date,
              index,
              isEditing && editApiDate === date ? editRecord?.entries : null,
              isEditing && editApiDate === date
                ? editRecord?.original_entries
                : null,
              isEditing && editApiDate === date ? editRecord?.early_exit : null,
            ),
          };
        }),
      );

      if (!active) return;

      const days = loaded.map((item) => item.day);
      setDefaultAttendanceDays(cloneAttendanceDays(days));
      const previousByDate = new Map(
        attendanceDaysRef.current.map((day) => [day.api_date, day]),
      );
      setAttendanceDays(
        days.map((day) => {
          const previous = previousByDate.get(day.api_date);
          return previous && (previous.punch_entries || []).length
            ? {
                ...day,
                punch_entries: previous.punch_entries,
                early_exit: previous.early_exit,
              }
            : day;
        }),
      );
      setOpenPunchDays(makeOpenState(days, true));
      setAttendanceLoadError(
        loaded.some((item) => !item.ok)
          ? "Some attendance details could not be loaded."
          : "",
      );
      setAttendanceLoading(false);
    };

    fetchAttendanceDays();

    return () => {
      active = false;
    };
  }, [
    domain,
    isShiftChange,
    open,
    rangeDates,
    requestDate,
    requestEndDate,
    effectiveUser?.id,
    topbarCompanyId,
  ]);

  useEffect(() => {
    if (!isShiftChange || !requestDate || isWeekOffSwap) return undefined;

    const fetchShiftOptions = async () => {
      setShiftOptionsLoading(true);
      const response = await customGetAPI(`${domain}all/shift-filter/`);
      const responseData = Array.isArray(response?.responseDetails)
        ? response.responseDetails
        : response?.responseDetails?.results || [];
      const options = response?.ok
        ? responseData.map((item) => ({
            ...item,
            id: item.shift_id ?? item.id,
            label: item.shift_name,
            start: item.start ?? item.start_time ?? "-",
            end: item.end ?? item.end_time ?? "-",
          }))
        : [];
      setShiftOptions(options);
      setShiftId((currentId) =>
        options.some((option) => option.id === currentId) ? currentId : "",
      );
      setShiftOptionsLoading(false);
    };

    fetchShiftOptions();
    return () => setShiftOptionsLoading(false);
  }, [domain, isShiftChange, isWeekOffSwap, requestDate]);

  // Resolve the user's schedule for the selected date(s). For a plain shift
  // change this stores the current shift as the original shift. For a week-off
  // swap it also captures the working day's shift, which is applied to the
  // original week-off date once approved.
  useEffect(() => {
    if (!isShiftChange || !requestDate || !userDetails?.id) {
      setAssignedShiftId(null);
      setAssignedShiftName("");
      setSwapShift(null);
      return;
    }

    const fetchAssignedShift = async () => {
      const rangeEnd =
        isWeekOffSwap && swapRequestDate ? swapRequestDate : requestEndDate;
      const from = dayjs(requestDate).isAfter(dayjs(rangeEnd))
        ? rangeEnd
        : requestDate;
      const to = dayjs(requestDate).isAfter(dayjs(rangeEnd))
        ? requestDate
        : rangeEnd;
      const url = `${domain}daily_user_shift_assignments/?selected_user_id=${userDetails.id}&from_date=${from}&to_date=${to}&company_id=${topbarCompanyId}`;
      const response = await customGetAPI(url);
      const rows = response?.responseDetails?.data;
      const shifts =
        Array.isArray(rows) && rows[0]?.shifts ? rows[0].shifts : [];
      const target = dayjs(requestDate).format("DD-MM-YYYY");
      const match = shifts.find((s) => s.date === target);
      const rawId = match?.shift_id;
      const validId =
        response?.ok &&
        rawId &&
        Number(rawId) !== defaultAllSelection &&
        match?.shift_name !== "Weekoff" &&
        match?.shift_name !== "Unassigned"
          ? rawId
          : null;
      setAssignedShiftId(validId);
      setAssignedShiftName(match?.shift_name || "");

      if (isWeekOffSwap && swapRequestDate) {
        const swapTarget = dayjs(swapRequestDate).format("DD-MM-YYYY");
        const swapMatch = shifts.find((s) => s.date === swapTarget);
        const swapRawId = swapMatch?.shift_id;
        const swapValidId =
          response?.ok &&
          swapRawId &&
          Number(swapRawId) !== defaultAllSelection &&
          swapMatch?.shift_name !== "Weekoff" &&
          swapMatch?.shift_name !== "Unassigned"
            ? swapRawId
            : null;
        setSwapShift(
          swapValidId
            ? {
                id: swapValidId,
                name: swapMatch?.shift_name || "Working Day",
                start: swapMatch?.shift_start_time,
                end: swapMatch?.shift_end_time,
              }
            : null,
        );
      } else {
        setSwapShift(null);
      }
    };

    fetchAssignedShift();
  }, [
    domain,
    isShiftChange,
    isWeekOffSwap,
    requestDate,
    requestEndDate,
    swapRequestDate,
    userDetails?.id,
    topbarCompanyId,
  ]);

  const handleDateRange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
    // The entries message names specific dates, so it goes stale with the
    // range; it's re-checked on the next Apply.
    setErrors((prev) => ({
      ...prev,
      date_range: undefined,
      entries: undefined,
    }));
  }, []);

  const updateAttendanceDay = (dayKey, updater) => {
    setAttendanceDays((prev) =>
      prev.map((day) => (day.api_date === dayKey ? updater(day) : day)),
    );
    setErrors((prev) =>
      prev.entries ? { ...prev, entries: undefined } : prev,
    );
  };

  const updateRow = (dayKey, rowId, patch) =>
    updateAttendanceDay(dayKey, (day) => ({
      ...day,
      punch_entries: (day.punch_entries || []).map((row) =>
        row.rowId === rowId ? { ...row, ...patch } : row,
      ),
    }));

  // A time change may move the row to the next day for an overnight shift
  // (see withOvernightDate). Other shifts keep the date the row shows, so an
  // early time on a day shift isn't silently dated to the next day.
  const updateRowTime = (dayKey, rowId, time) =>
    updateAttendanceDay(dayKey, (day) => {
      const entries = day.punch_entries || [];
      if (!isOvernightShift(day.shift_start, day.shift_end)) {
        return {
          ...day,
          punch_entries: entries.map((row) =>
            row.rowId === rowId ? { ...row, time } : row,
          ),
        };
      }
      const patch = withOvernightDate(
        entries,
        rowId,
        { time },
        maxPunchDateFor(day.api_date),
      );
      return {
        ...day,
        punch_entries: entries.map((row) =>
          row.rowId === rowId ? { ...row, ...patch } : row,
        ),
      };
    });

  const addRow = (dayKey) =>
    updateAttendanceDay(dayKey, (day) => {
      // Corrected punches replace the whole day on approval, so the first "+"
      // on a day that already has punches copies them in and adds the next
      // punch after them (e.g. OUT after a lone IN), instead of starting an
      // empty set that would drop the punches on record.
      const entries =
        (day.punch_entries || []).length ||
        !(day.original_punch_entries || []).length
          ? day.punch_entries || []
          : day.original_punch_entries.map((entry, index) => ({
              ...entry,
              rowId: index + 1,
              date: entry.date ? dayjs(entry.date) : null,
              time: entry.time ? dayjs(entry.time) : null,
            }));
      const nextRowId =
        entries.reduce(
          (max, entry) => Math.max(max, Number(entry.rowId) || 0),
          0,
        ) + 1;
      const lastEntry = entries[entries.length - 1];
      const lastDirection = lastEntry?.direction || "OUT";
      // Continue on the last punch's date, so after an overnight shift's
      // next-day punch the following one isn't dated back to the AR date.
      const lastDate =
        lastEntry?.date && dayjs(lastEntry.date).isValid()
          ? dayjs(lastEntry.date)
          : null;
      return {
        ...day,
        punch_entries: [
          ...entries,
          {
            ...makePunchRow(
              nextRowId,
              lastDate || toDayjsDate(day.api_date) || dayjs(),
            ),
            direction: lastDirection === "OUT" ? "IN" : "OUT",
          },
        ],
      };
    });

  const removeRow = (dayKey, rowId) =>
    updateAttendanceDay(dayKey, (day) => ({
      ...day,
      punch_entries: (day.punch_entries || []).filter(
        (row) => row.rowId !== rowId,
      ),
      early_exit:
        (day.punch_entries || []).length <= 1 ? false : Boolean(day.early_exit),
    }));

  const updateEarlyExit = (dayKey, checked) =>
    updateAttendanceDay(dayKey, (day) => ({
      ...day,
      early_exit: checked,
    }));

  const togglePunchDay = (dayKey) =>
    setOpenPunchDays((prev) => ({ ...prev, [dayKey]: !prev[dayKey] }));

  const resetForm = () => {
    if (!isShiftChange) {
      setAttendanceDays(cloneAttendanceDays(defaultAttendanceDays));
      setOpenPunchDays(makeOpenState(defaultAttendanceDays, true));
      setReason("");
      setErrors({});
      return;
    }

    const today = todayDisplay();
    setFromDate(today);
    setToDate(today);
    setSwapDate(today);
    setRequestType("shift_change");
    setShiftId("");
    setShiftOptions([]);
    setAssignedShiftId(null);
    setReason("");
    setErrors({});
  };

  const validate = () => {
    const next = {};
    if (employeePickerActive && !pickedEmployee?.id) {
      next.employee = "Select an employee";
    }
    if (!requestDate || !requestEndDate) {
      next.date_range = isShiftChange
        ? "Date range is required"
        : "AR date range is required";
    }
    if (isShiftChange) {
      if (isWeekOffSwap) {
        if (!swapRequestDate) next.swap_date = "Move week-off date is required";
        if (swapRequestDate && swapRequestDate === requestDate) {
          next.swap_date = "Move week-off date must be different";
        }
        if (!swapShift?.id) {
          next.swap_date = "Select a working day with an assigned shift";
        }
      } else if (!shiftId) next.shift = "Shift name is required";
    } else {
      // Only days the user actually corrected are submitted; a multi-day range
      // doesn't need a punch on every day.
      const correctedDays = attendanceDays.filter((day) =>
        hasTimedPunch(day.punch_entries),
      );
      const sequenceError = correctedDays.map(punchSequenceError).find(Boolean);
      const timedWithoutDate = correctedDays.find((day) =>
        (day.punch_entries || []).some(
          (entry) =>
            entry.time &&
            dayjs(entry.time).isValid() &&
            (!entry.date || !dayjs(entry.date).isValid()),
        ),
      );
      if (attendanceLoading) {
        next.entries = "Attendance details are still loading";
      } else if (!attendanceDays.length) {
        next.entries = "No attendance details found";
      } else if (!correctedDays.length) {
        next.entries =
          attendanceDays.length > 1
            ? "Add at least one punch time for a day in the selected range"
            : `Add at least one punch time for ${attendanceDays[0].display_date}`;
      } else if (timedWithoutDate) {
        next.entries = `Select a date for each punch time in ${timedWithoutDate.display_date}`;
      } else if (sequenceError) {
        next.entries = sequenceError;
      }
    }
    const trimmed = reason.trim();
    if (!trimmed) next.reason = "Reason is required";
    else if (trimmed.length < 3) next.reason = "Minimum 3 characters";
    else if (trimmed.length > REASON_MAX_LENGTH) {
      next.reason = `Maximum length is ${REASON_MAX_LENGTH} characters`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    // Shift Change persists to the My Zone apply endpoint. The backend stores a
    // single date per request, so the range start (requestDate) is sent as
    // `date`; the selected shift becomes `requested_shift_id`.
    if (isShiftChange) {
      const requestedShiftId = isWeekOffSwap
        ? swapShift?.id
        : selectedShift?.id;
      const payload = {
        correction_type: defaultCorrectionType,
        request_type: requestType,
        from_date: requestDate,
        to_date: isWeekOffSwap ? swapRequestDate : requestEndDate,
        shift: {
          id: requestedShiftId,
          name: isWeekOffSwap ? swapShift?.name : selectedShift?.label,
          start: isWeekOffSwap ? swapShift?.start : selectedShift?.start,
          end: isWeekOffSwap ? swapShift?.end : selectedShift?.end,
        },
        entries: [],
        early_exit: false,
        reason: reason.trim(),
      };
      const apiPayload = isEditing
        ? {
            id: editRecord.id,
            request_type: requestType,
            date: requestDate,
            swap_date: isWeekOffSwap ? swapRequestDate : null,
            assigned_shift_id: isWeekOffSwap ? swapShift?.id : assignedShiftId,
            requested_shift_id: requestedShiftId,
            reason: reason.trim(),
          }
        : {
            user_id: userDetails?.id,
            request_type: requestType,
            date: requestDate,
            swap_date: isWeekOffSwap ? swapRequestDate : null,
            assigned_shift_id: isWeekOffSwap ? swapShift?.id : assignedShiftId,
            requested_shift_id: requestedShiftId,
            reason: reason.trim(),
          };
      const response = await customAPI(
        `${domain}my_zone/apply/shift_change/myzone-shift-change/?company_id=${topbarCompanyId}`,
        isEditing ? "PUT" : "POST",
        apiPayload,
      );
      if (response?.ok) {
        onCreate(buildRecord ? buildRecord(payload) : payload);
        showToast(
          "success",
          response?.responseDetails?.message ||
            (isEditing
              ? "Schedule change request updated"
              : "Schedule change request submitted"),
        );
        resetForm();
        onClose();
      }
      // On failure customAPI already surfaces the error toast; keep the panel
      // open so the user can retry.
      setSubmitting(false);
      return;
    }

    const dayPayloads = attendanceDays
      .filter((day) => hasTimedPunch(day.punch_entries))
      .map((day) => {
        const originalPunch = deriveInOut(day.original_punch_entries || []);
        const correctedPunch = deriveInOut(day.punch_entries || []);
        return {
          correction_type: defaultCorrectionType,
          date: day.api_date,
          from_date: day.api_date,
          to_date: day.api_date,
          attendance_record_id: day.attendance_record_id ?? null,
          original_in: originalPunch.corrected_in,
          original_out: originalPunch.corrected_out,
          corrected_in: correctedPunch.corrected_in,
          corrected_out: correctedPunch.corrected_out,
          shift: {
            id: day.shift,
            name: day.shift_name,
            start: day.shift_start,
            end: day.shift_end,
          },
          entries: toPunchPayloadRows(day.punch_entries, day.api_date),
          early_exit: Boolean(day.early_exit),
          reason: reason.trim(),
        };
      });

    const payload = {
      correction_type: defaultCorrectionType,
      from_date: requestDate,
      to_date: requestEndDate,
      days: dayPayloads,
      entries: dayPayloads.flatMap((day) => day.entries),
      early_exit: dayPayloads.some((day) => day.early_exit),
      reason: reason.trim(),
    };

    // Attendance Correction persists one request per day to the My Zone endpoint.
    // The stored request stays "Pending" until a manager approves it in Team Zone.
    const results = await Promise.all(
      dayPayloads.map((day) =>
        customAPI(
          `${domain}my_zone/apply/attendance_correction/myzone-attendance-correction/?company_id=${topbarCompanyId}`,
          "POST",
          {
            user_id: effectiveUser?.id,
            date: day.date,
            attendance_record_id: day.attendance_record_id,
            shift_id: day.shift?.id ?? null,
            reason: reason.trim(),
            entries: day.entries,
            early_exit: Boolean(day.early_exit),
            // Always created Pending; approveOnCreate approves it below only
            // when the caller holds approver permission.
            status: "Pending",
            // Edit replaces this day's Pending request; without it the API
            // refuses a second request for a day that already has one.
            ...(isEditing && { edit: true }),
          },
        ),
      ),
    );

    if (!results.every((response) => response?.ok)) {
      // customAPI already surfaced the error toast; keep the panel open to retry.
      setSubmitting(false);
      return;
    }

    let approvalFailedMessage = null;
    if (approveOnCreate && !isEditing) {
      const approvalResults = await Promise.all(
        dayPayloads.map((day) =>
          customAPI(
            `${domain}team_zone/approve/attendance_correction/teamzone-attendance-correction/`,
            "PUT",
            {
              user_id: effectiveUser?.id,
              date: day.date,
              status: "Approved",
              early_exit: Boolean(day.early_exit),
            },
          ),
        ),
      );
      const failed = approvalResults.find((response) => !response?.ok);
      if (failed) {
        // The request itself was created successfully -- only the immediate
        // approval step failed (e.g. the picked employee is the approver's
        // own self-request, which the API blocks). Don't lose that: fall
        // through to onCreate/close below, just with a message that says so
        // instead of claiming it's approved.
        approvalFailedMessage =
          failed?.message ||
          failed?.responseDetails?.error ||
          "Request created, but couldn't be approved automatically. Approve it from the list.";
      }
    }

    onCreate(buildRecord ? buildRecord(payload) : payload);
    showToast(
      approvalFailedMessage ? "error" : "success",
      approvalFailedMessage ||
        (isEditing
          ? "Correction request updated"
          : approveOnCreate
            ? "Attendance correction approved"
            : dayPayloads.length > 1
              ? "Correction requests submitted"
              : "Correction request submitted"),
    );
    setSubmitting(false);
    resetForm();
    onClose();
  };

  const footerError =
    errors.employee ||
    errors.date_range ||
    errors.swap_date ||
    errors.shift ||
    errors.entries ||
    errors.reason ||
    "";

  const punchDatePickerSlot = {
    textField: { size: "small", fullWidth: true },
  };

  const renderAttendancePunchDay = (day) => {
    const isOpen = openPunchDays[day.api_date] ?? true;
    const cardBaseDate = toDayjsDate(day.api_date);
    const maxPunchDate = maxPunchDateFor(day.api_date);
    const shiftRowBg = day.is_week_off
      ? day.week_off_color
      : day.is_holiday
        ? day.holiday_color
        : "#F4F5F2";

    return (
      <Box
        key={day.api_date}
        sx={{
          border: `1px solid ${leavePalette.border}`,
          borderRadius: 1.5,
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <Grid
          container
          alignItems="center"
          sx={{
            bgcolor: shiftRowBg,
            px: 1,
            py: 1.25,
            borderBottom: isOpen ? `1px solid ${leavePalette.border}` : "none",
          }}
        >
          <Grid item xs={12} sm={4} sx={{ px: 0.5 }}>
            <Typography sx={sectionLabelSx}>Shift Name</Typography>
            {/* Read-only, shown like Shift Time -- it isn't editable here. */}
            <Typography sx={{ mt: 0.75 }}>{day.shift_name || "-"}</Typography>
          </Grid>
          <Grid
            item
            xs={5}
            sm={3.5}
            sx={{ textAlign: "center", mt: { xs: 1.5, sm: 0 } }}
          >
            <Typography sx={sectionLabelSx}>Shift Time</Typography>
            <Typography sx={{ mt: 0.75 }}>
              {day.shift_start || day.shift_end
                ? `${day.shift_start || "-"} to ${day.shift_end || "-"}`
                : "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={5}
            sm={3.5}
            sx={{ textAlign: "center", mt: { xs: 1.5, sm: 0 } }}
          >
            <Typography sx={sectionLabelSx}>AR Date</Typography>
            <Typography sx={{ mt: 0.75 }}>{day.display_date}</Typography>
          </Grid>
          <Grid item xs={2} sm={1} sx={{ textAlign: "right" }}>
            <IconButton
              size="small"
              onClick={() => togglePunchDay(day.api_date)}
              aria-label={
                isOpen ? "collapse punch entries" : "expand punch entries"
              }
            >
              <ExpandMoreIcon
                sx={{
                  transition: "transform .2s ease",
                  transform: isOpen ? "rotate(180deg)" : "none",
                }}
              />
            </IconButton>
          </Grid>
        </Grid>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Typography
            sx={{
              px: 1,
              pt: 1.25,
              pb: 0.5,
              fontSize: 11.5,
              fontWeight: 700,
              color: leavePalette.subText,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Current Punches
          </Typography>
          <CurrentPunchesTable entries={day.original_punch_entries || []} />
          <Typography
            sx={{
              px: 1,
              pt: 1,
              fontSize: 11.5,
              fontWeight: 700,
              color: leavePalette.subText,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Corrected Punches
          </Typography>
          <Stack>
            {(day.punch_entries || []).map((row) => (
              <Stack
                key={row.rowId}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{
                  px: 1,
                  py: 1,
                  borderBottom: `1px solid ${leavePalette.border}`,
                }}
              >
                <Box sx={{ flex: PUNCH_COLUMN_FLEX.date }}>
                  <DatePicker
                    value={row.date || null}
                    format="DD-MM-YYYY"
                    minDate={cardBaseDate || undefined}
                    maxDate={maxPunchDate || undefined}
                    onChange={(val) =>
                      updateRow(day.api_date, row.rowId, {
                        date: val,
                        autoRolledDate: false,
                      })
                    }
                    slotProps={punchDatePickerSlot}
                  />
                </Box>
                <Box sx={{ flex: PUNCH_COLUMN_FLEX.time }}>
                  <TimePicker
                    ampm={false}
                    format="HH:mm"
                    views={["hours", "minutes"]}
                    openTo="hours"
                    value={row.time || null}
                    onChange={(val) =>
                      updateRowTime(day.api_date, row.rowId, val)
                    }
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                    }}
                    slotProps={punchDatePickerSlot}
                  />
                </Box>
                <Box sx={{ flex: PUNCH_COLUMN_FLEX.direction }}>
                  <Autocomplete
                    disablePortal
                    disableClearable
                    size="small"
                    value={
                      DIRECTION_OPTIONS.find((d) => d.id === row.direction) ||
                      DIRECTION_OPTIONS[0]
                    }
                    options={DIRECTION_OPTIONS}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(e, val) =>
                      val &&
                      updateRow(day.api_date, row.rowId, { direction: val.id })
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removeRow(day.api_date, row.rowId)}
                  aria-label="remove punch"
                  sx={{
                    alignSelf: { xs: "flex-end", sm: "center" },
                    width: PUNCH_REMOVE_SLOT_WIDTH,
                    height: PUNCH_REMOVE_SLOT_WIDTH,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>

          <Box sx={{ px: 1, py: 0.75 }}>
            <IconButton
              size="small"
              onClick={() => addRow(day.api_date)}
              aria-label="add punch"
            >
              <AddCircleOutlineIcon
                sx={{ color: leavePalette.blue }}
                fontSize="small"
              />
            </IconButton>
          </Box>

          <Divider />
          <FormControlLabel
            sx={{
              m: 0,
              px: 1,
              py: 1.25,
              ".MuiFormControlLabel-label": {
                fontWeight: 600,
                color: leavePalette.text,
              },
            }}
            control={
              <Checkbox
                checked={Boolean(day.early_exit)}
                onChange={(e) =>
                  updateEarlyExit(day.api_date, e.target.checked)
                }
                disabled={(day.punch_entries || []).length === 0}
              />
            }
            label="Early Exit"
          />
        </Collapse>
      </Box>
    );
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={isEditing ? undefined : panelTitle}
      subtitle={isEditing ? undefined : panelSubtitle}
      header={
        isEditing ? (
          <RequestEditHeader
            title={
              isShiftChange
                ? "Schedule Change Request"
                : "Attendance Correction Request"
            }
          />
        ) : undefined
      }
      footer={
        <Stack spacing={1} sx={{ width: "100%" }}>
          {/* The field messages can be scrolled out of view, so repeat the
            first one next to the button that triggered it. */}
          {footerError ? (
            <Typography
              role="alert"
              sx={{ fontSize: 12.5, color: leavePalette.red }}
            >
              {footerError}
            </Typography>
          ) : null}
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <SecondaryButton
              onClick={isEditing || isShiftChange ? onClose : resetForm}
              disabled={submitting}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {isEditing ? "Discard" : isShiftChange ? "Cancel" : "Reset"}
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {isEditing ? "Update" : approveOnCreate ? "Approve" : "Apply"}
            </PrimaryButton>
          </Stack>
        </Stack>
      }
      bodySx={{ backgroundColor: designColors.pageBg }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {employeePickerActive ? (
            <Box>
              <GlobalFieldLabel helperText="Select the employee for whom the Attendance Correction request is being created.">
                Employee Name
              </GlobalFieldLabel>
              <CustomAutoCompleteWithLogo
                dataList={pickerEmployees?.map((obj) => ({
                  ...obj,
                  id: obj?.user_id,
                  label: obj?.user_name,
                  image: obj?.image_url,
                }))}
                selectedValue={
                  pickedEmployee
                    ? { ...pickedEmployee, label: pickedEmployee.user_name }
                    : null
                }
                updateDataFunction={(event, data) => {
                  setPickedEmployee(
                    data
                      ? {
                          id: data.user_id,
                          employee_id: data.employee_id,
                          user_name: data.user_name,
                          department_name:
                            data.department_name || data.department,
                          designation:
                            data.designation_name || data.designation,
                          image_url: data.image_url,
                        }
                      : null,
                  );
                  setErrors((prev) => ({ ...prev, employee: undefined }));
                }}
                placeHolder="Select employee name"
                errorMessage={errors.employee || ""}
                textBoxError={Boolean(errors.employee)}
              />
            </Box>
          ) : null}

          <RequestDetailHeader
            userDetails={{
              image_url: editRecord?.image_url || effectiveUser?.image_url,
              employee_id:
                editRecord?.employee_id ||
                editRecord?.emp_id ||
                effectiveUser?.employee_id ||
                "-",
              user_name:
                editRecord?.employee_name ||
                editRecord?.user_name ||
                effectiveUser?.first_name ||
                effectiveUser?.user_name ||
                effectiveUser?.username ||
                "-",
              department_name:
                editRecord?.department_name ||
                editRecord?.department ||
                effectiveUser?.department_name ||
                effectiveUser?.department ||
                "-",
              designation:
                editRecord?.designation_name ||
                editRecord?.designation ||
                effectiveUser?.designation ||
                effectiveUser?.designation_name ||
                "-",
            }}
            status={editRecord?.status}
            colorCode={editRecord?.color_code}
          />

          {isShiftChange ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                px: 0.5,
              }}
            >
              {!isEditing ? (
                <Box>
                  <Typography sx={sectionLabelSx}>Request Type</Typography>
                  <Typography sx={helperSx}>
                    Select the type of schedule change you want to request
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    {Object.values(SCHEDULE_REQUEST_TYPES).map((type) => {
                      const active = requestType === type.key;
                      const Icon = type.icon || CalendarMonthOutlinedIcon;
                      return (
                        <Box
                          key={type.key}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setRequestType(type.key);
                            setErrors({});
                            if (type.key === "week_off_swap") setShiftId("");
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setRequestType(type.key);
                            }
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: active
                              ? type.color
                              : leavePalette.border,
                            borderRadius: 1,
                            p: 1.5,
                            cursor: "pointer",
                            bgcolor: active ? `${type.color}12` : "#fff",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Icon
                              sx={{
                                color: active
                                  ? type.color
                                  : leavePalette.subText,
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  color: active
                                    ? type.color
                                    : leavePalette.text,
                                }}
                              >
                                {type.label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 12.5,
                                  color: leavePalette.subText,
                                }}
                              >
                                {type.key === "week_off_swap"
                                  ? "Work on week off and move it"
                                  : "Change to a different shift"}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : null}

              <Box>
                <Typography sx={sectionLabelSx}>
                  {isWeekOffSwap ? "Work On (Original Week Off)" : "Date"}
                </Typography>
                <Typography sx={helperSx}>
                  {isWeekOffSwap
                    ? "Select the week off date you will work"
                    : "Select the date range for the schedule change"}
                </Typography>
                <Box sx={{ maxWidth: { xs: "100%", sm: 384 } }}>
                  {isWeekOffSwap ? (
                    <DatePicker
                      value={displayDateToDayjs(fromDate)}
                      format="DD-MM-YYYY"
                      onChange={(value) => {
                        const next =
                          value && value.isValid()
                            ? value.format(DATE_DISPLAY_FMT)
                            : "";
                        setFromDate(next);
                        setToDate(next);
                        setErrors((prev) => ({
                          ...prev,
                          date_range: undefined,
                        }));
                      }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  ) : (
                    <CustomDateRangePicker
                      handleStartDate={handleDateRange}
                      showToday
                      triggerDate
                      includeCurrentDate
                      fromDate={fromDate}
                      toDate={toDate}
                      hideLabel
                      compact
                    />
                  )}
                </Box>
                {errors.date_range ? (
                  <Typography
                    sx={{ fontSize: 12, color: leavePalette.red, mt: 0.5 }}
                  >
                    {errors.date_range}
                  </Typography>
                ) : null}
              </Box>

              {isWeekOffSwap ? (
                <Box>
                  <Typography sx={sectionLabelSx}>Move Week Off To</Typography>
                  <Typography sx={helperSx}>
                    Select the working day that will become the replacement week
                    off
                  </Typography>
                  <Box sx={{ maxWidth: { xs: "100%", sm: 384 } }}>
                    <DatePicker
                      value={displayDateToDayjs(swapDate)}
                      format="DD-MM-YYYY"
                      onChange={(value) => {
                        setSwapDate(
                          value && value.isValid()
                            ? value.format(DATE_DISPLAY_FMT)
                            : "",
                        );
                        setErrors((prev) => ({
                          ...prev,
                          swap_date: undefined,
                        }));
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: Boolean(errors.swap_date),
                          helperText: errors.swap_date || "",
                        },
                      }}
                    />
                  </Box>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    sx={{ mt: 1 }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "#EEF4FF",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 12, color: leavePalette.subText }}
                      >
                        Original date
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        {assignedShiftName === "Weekoff"
                          ? "Week Off"
                          : assignedShiftName || "Week Off"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "#ECFDF3",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 12, color: leavePalette.subText }}
                      >
                        Move-to date
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        {swapShift?.name || "Select a working day"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography sx={sectionLabelSx}>Current Shift</Typography>
                  <Typography sx={helperSx}>
                    This is your currently assigned shift for the selected date
                  </Typography>
                  <TextField
                    size="small"
                    value={assignedShiftName || "No assigned shift"}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{ maxWidth: { xs: "100%", sm: 384 } }}
                  />
                </Box>
              )}

              {!isWeekOffSwap ? (
                <Box>
                  <Typography sx={sectionLabelSx}>Shift Name</Typography>
                  <Typography sx={helperSx}>
                    Select the shift to apply for the selected date range
                  </Typography>
                  <Autocomplete
                    disablePortal
                    size="small"
                    value={selectedShift}
                    options={shiftOptions}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(e, val) => {
                      setShiftId(val?.id || "");
                      setErrors((prev) => ({ ...prev, shift: undefined }));
                    }}
                    sx={{ maxWidth: { xs: "100%", sm: 384 } }}
                    loading={shiftOptionsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select shift name"
                        size="small"
                        error={Boolean(errors.shift)}
                        helperText={errors.shift || ""}
                      />
                    )}
                  />
                </Box>
              ) : null}
            </Box>
          ) : (
            <>
              <Grid
                container
                spacing={1.5}
                alignItems="center"
                sx={{ px: 0.5 }}
              >
                <Grid item xs={12} sm={3}>
                  <Typography sx={sectionLabelSx}>AR Date Range</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomDateRangePicker
                    handleStartDate={handleDateRange}
                    showToday
                    triggerDate
                    includeCurrentDate
                    fromDate={fromDate}
                    toDate={toDate}
                    hideLabel
                    compact
                  />
                  {errors.date_range ? (
                    <Typography
                      sx={{ fontSize: 12, color: leavePalette.red, mt: 0.5 }}
                    >
                      {errors.date_range}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    sx={{ fontWeight: 700, color: leavePalette.text }}
                  >
                    Punch Entries
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Expand all punch entries">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setOpenPunchDays(
                              makeOpenState(attendanceDays, true),
                            )
                          }
                          disabled={!attendanceDays.length || attendanceLoading}
                          aria-label="expand all punch entries"
                        >
                          <UnfoldMoreIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Collapse all punch entries">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setOpenPunchDays(
                              makeOpenState(attendanceDays, false),
                            )
                          }
                          disabled={!attendanceDays.length || attendanceLoading}
                          aria-label="collapse all punch entries"
                        >
                          <UnfoldLessIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                {attendanceLoadError ? (
                  <Typography
                    sx={{ fontSize: 12, color: leavePalette.red, mb: 1 }}
                  >
                    {attendanceLoadError}
                  </Typography>
                ) : null}
                {errors.entries ? (
                  <Typography
                    sx={{ fontSize: 12, color: leavePalette.red, mb: 1 }}
                  >
                    {errors.entries}
                  </Typography>
                ) : null}

                {attendanceLoading ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      border: `1px solid ${leavePalette.border}`,
                      borderRadius: 1.5,
                      px: 2,
                      py: 1.75,
                      bgcolor: "#fff",
                    }}
                  >
                    <CircularProgress size={18} />
                    <Typography
                      sx={{ fontSize: 13, color: leavePalette.subText }}
                    >
                      Loading attendance details
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    {attendanceDays.map(renderAttendancePunchDay)}
                  </Stack>
                )}
              </Box>
            </>
          )}

          {/* Reason */}
          <Box>
            <Typography sx={sectionLabelSx}>Reason</Typography>
            <Typography sx={helperSx}>
              {isShiftChange
                ? "Enter the reason for this schedule change"
                : "Enter the reason for this correction"}
            </Typography>
            <TextField
              multiline
              minRows={3}
              fullWidth
              size="small"
              value={reason}
              // Truncate instead of ignoring the change -- pasting a longer text
              // used to leave the field empty with no message.
              onChange={(e) => {
                setReason(e.target.value.slice(0, REASON_MAX_LENGTH));
                if (errors.reason)
                  setErrors((prev) => ({ ...prev, reason: undefined }));
              }}
              inputProps={{ maxLength: REASON_MAX_LENGTH }}
              placeholder={
                isShiftChange
                  ? "Provide a reason for the schedule change"
                  : "Provide a reason for the correction"
              }
              error={Boolean(errors.reason)}
              helperText={
                errors.reason ||
                `${reason.length}/${REASON_MAX_LENGTH}${
                  reason.length >= REASON_MAX_LENGTH ? " — limit reached" : ""
                }`
              }
            />
          </Box>
        </Box>
      </LocalizationProvider>
    </GlobalSidePanel>
  );
};

export default CorrectionApplyPanel;
